import { formatReadableDate } from '~/utils/format'
import i18next from '~/i18n'
import { addDays, setHours, setMinutes, setSeconds } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'

const STOCKHOLM_TZ = 'Europe/Stockholm'

// export const isFixedPriceAvailable = (values, type = false) => {
//   const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')
//   return values.availability[key]?.priceAndPaymentRules?.priceTypes?.find(item => item.type === 'FIXED') !== undefined
// }

// export const isTaximeterAvailable = (values, type = false) => {
//   const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')
//   return values.availability[key]?.priceAndPaymentRules?.priceTypes?.find(item => item.type === 'TAXIMETER') !== undefined
// }

export const isEditMode = (values) => {
  return !isNaN(values.bookingId) && values.bookingId > 0
}

// For v4 multi-provider responses, merge servicesByProvider into a flat structure
// so all existing logic (getAutoValues, priceType effects) works without changes.
// The raw servicesByProvider array is preserved on the object for PartnerSelection.
export const normalizeAvailability = (availability) => {
  if (!availability?.servicesByProvider) return availability
  const providers = availability.servicesByProvider
  const statusPriority = { AS_REQUESTED: 2, DELAYED: 1, NOT_AVAILABLE: 0 }

  const byServiceType = {}
  for (const provider of providers) {
    for (const a of provider.availabilities || []) {
      const current = byServiceType[a.serviceType]
      if (!current || statusPriority[a.availableAt] > statusPriority[current.availableAt]) {
        byServiceType[a.serviceType] = {
          ...a,
          vehicleOptions: [...new Set([...(current?.vehicleOptions || []), ...(a.vehicleOptions || [])])],
        }
      } else {
        current.vehicleOptions = [...new Set([...(current.vehicleOptions || []), ...(a.vehicleOptions || [])])]
      }
    }
  }

  const priceTypeMap = {}
  for (const provider of providers) {
    for (const pt of provider.priceAndPaymentRules?.priceTypes || []) {
      if (!priceTypeMap[pt.type]) priceTypeMap[pt.type] = pt
    }
  }

  const maxStops = providers.reduce((max, p) => {
    const n = p.tripRules?.maxAllowedIntermediateStops
    return n !== undefined ? Math.max(max, n) : max
  }, availability.tripRules?.maxAllowedIntermediateStops ?? 0)

  const maxLatestPrebook = providers.reduce((max, p) => {
    const d = p.tripRules?.latestPrebookingDateTime
    if (!d) return max
    if (!max) return d
    return new Date(d) > new Date(max) ? d : max
  }, availability.tripRules?.latestPrebookingDateTime ?? null)

  const maxEarliestPrebook = providers.reduce((max, p) => {
    const d = p.tripRules?.earliestPrebookingDateTime
    if (!d) return max
    if (!max) return d
    return new Date(d) > new Date(max) ? d : max
  }, availability.tripRules?.earliestPrebookingDateTime ?? null)

  // Boolean flags: true if ANY provider requires it (most restrictive wins)
  const requiresDestForPrebook = providers.some(p => p.tripRules?.preBookingRequiresDestination === true)
  const requiresDestForDirect = providers.some(p => p.tripRules?.directBookingRequiresDestination === true)

  return {
    ...availability,
    availabilities: Object.values(byServiceType),
    priceAndPaymentRules: { priceTypes: Object.values(priceTypeMap) },
    tripRules: {
      ...availability.tripRules,
      maxAllowedIntermediateStops: maxStops,
      ...(maxLatestPrebook ? { latestPrebookingDateTime: maxLatestPrebook } : {}),
      ...(maxEarliestPrebook ? { earliestPrebookingDateTime: maxEarliestPrebook } : {}),
      ...(requiresDestForPrebook ? { preBookingRequiresDestination: true } : {}),
      ...(requiresDestForDirect ? { directBookingRequiresDestination: true } : {}),
    },
  }
}

export const isFieldEditable = (values, fieldPath) => {
  if (!isEditMode(values)) return true // All fields editable in new booking mode
  return values.editableFields?.includes(fieldPath) || false
}

export const getAvailableVehicleOptions = (values) => {
  // In edit mode, only return options that are editable
  if (isEditMode(values) && values.editableOptions?.vehicleOptions) {
    return values.editableOptions.vehicleOptions.map(opt => opt.vehicleOption)
  }

  const key = values.pickupTime === '' ? 'direct' : 'prebook'
  const type = (values?.autoValues?.serviceType || values?.serviceType) === 'estate' ? 'taxi' : (values?.autoValues?.serviceType || values?.serviceType)

  // Always use the normalized availabilities which already union vehicleOptions across all providers
  const availabilities = values.availability?.[key]?.availabilities
  if (!availabilities || !Array.isArray(availabilities)) return []
  const options = availabilities.find(item => item.serviceType === type)?.vehicleOptions
  return options !== undefined ? options : []
}

export const getVehicleOptionPrice = (values, vehicleOption) => {
  const key = values.pickupTime === '' ? 'direct' : 'prebook'
  const type = (values?.autoValues?.serviceType || values?.serviceType) === 'estate' ? 'taxi' : (values?.autoValues?.serviceType || values?.serviceType)

  // In multi-provider mode, use the selected provider's price if available,
  // otherwise fall back to the highest price across all providers (the option will
  // auto-switch to a provider that charges for it, so show that cost as a hint)
  const servicesByProvider = values.availability?.[key]?.servicesByProvider
  if (Array.isArray(servicesByProvider) && values.selectedProviderId) {
    const provider = servicesByProvider.find(p => p.providerData.providerId === values.selectedProviderId)
    const price = provider?.availabilities?.find(a => a.serviceType === type)?.chargeableVehicleOptions?.find(c => c.vehicleOption === vehicleOption)?.price
    if (price !== undefined) return price
    // Selected provider has no price — return the highest across all providers
    const maxPrice = servicesByProvider.reduce((max, p) => {
      const p2 = p.availabilities?.find(a => a.serviceType === type)?.chargeableVehicleOptions?.find(c => c.vehicleOption === vehicleOption)?.price
      return p2 !== undefined && p2 > max ? p2 : max
    }, 0)
    return maxPrice
  }

  const availabilities = values.availability?.[key]?.availabilities
  if (!availabilities || !Array.isArray(availabilities)) return 0
  const price = availabilities.find(item => item.serviceType === type)?.chargeableVehicleOptions?.find(item => item.vehicleOption === vehicleOption)?.price
  return price !== undefined ? price : 0
}

export const getMaxAllowedIntermediateStops  = (values) => {
  const key = values.pickupTime === '' ? 'direct' : 'prebook'
  if (values.availability[key] !== false) {
    const a = values.availability[key]
    return a?.tripRules?.maxAllowedIntermediateStops !== undefined ? a.tripRules.maxAllowedIntermediateStops : 0
  }
  return 0
}

export const hasInformationTag = (values, tag) => {
  return values.departure?.locationInformation?.filter(info => info?.tag === tag)?.length > 0
}

// Returns locationInformation items to show as departure notifications.
// New tags DEPARTURE_INFO_PREBOOK / DEPARTURE_INFO_DIRECT take precedence
// over the legacy DEPARTURE_INFO tag. When showBothBookingTypes is true (start
// page, before booking type is chosen) both new tags are included.
export const getDepartureNotificationItems = (locationInformation, pickupTime, showBothBookingTypes = false) => {
  if (!locationInformation) return []

  const hasNewTags = locationInformation.some(item =>
    item?.tag === 'DEPARTURE_INFO_PREBOOK' || item?.tag === 'DEPARTURE_INFO_DIRECT'
  )

  // INFO tag is always included regardless of booking type
  const infoItems = locationInformation.filter(item => item?.tag === 'INFO')

  if (!hasNewTags) {
    return locationInformation.filter(item => ['DEPARTURE_INFO', 'INFO'].includes(item?.tag))
  }

  if (showBothBookingTypes) {
    return [
      ...locationInformation.filter(item =>
        item?.tag === 'DEPARTURE_INFO_PREBOOK' || item?.tag === 'DEPARTURE_INFO_DIRECT'
      ),
      ...infoItems,
    ]
  }

  const tag = pickupTime ? 'DEPARTURE_INFO_PREBOOK' : 'DEPARTURE_INFO_DIRECT'
  return [
    ...locationInformation.filter(item => item?.tag === tag),
    ...infoItems,
  ]
}

export const getLatestPrebookingDateTime = (values) => {
  // If we're in SelectTime, we're selecting a future time, so use 'prebook' key
  const key = 'prebook'
  
  if (values?.availability && values.availability[key] !== false && values.availability[key] !== undefined) {
    const a = values.availability[key]
    if (a?.tripRules?.latestPrebookingDateTime) {
      const latestDateString = a.tripRules.latestPrebookingDateTime
      console.log('[getLatestPrebookingDateTime] Using API date:', latestDateString)
      // Parse the date string directly to extract just the date part (YYYY-MM-DD)
      // This avoids timezone conversion issues
      const dateMatch = latestDateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateMatch) {
        const year = parseInt(dateMatch[1], 10)
        const month = parseInt(dateMatch[2], 10) - 1 // JavaScript months are 0-indexed
        const day = parseInt(dateMatch[3], 10)
        // Create 23:59:59 in Stockholm time for that calendar date
        const endOfDay = zonedTimeToUtc(new Date(year, month, day, 23, 59, 59, 0), STOCKHOLM_TZ)
        console.log('[getLatestPrebookingDateTime] End of day:', endOfDay)
        return endOfDay
      }
      // Fallback: parse the full datetime and extract calendar date in Stockholm timezone
      const latestDate = new Date(latestDateString)
      const stockholmStr = latestDate.toLocaleDateString('sv-SE', { timeZone: STOCKHOLM_TZ })
      const [y, mo, d] = stockholmStr.split('-').map(Number)
      return zonedTimeToUtc(new Date(y, mo - 1, d, 23, 59, 59, 0), STOCKHOLM_TZ)
    }
  }
  // Fallback: if availability not loaded yet, use a very far future date to avoid false validation errors
  // The validation will be updated once availability loads
  console.log('[getLatestPrebookingDateTime] Availability not loaded, using far future fallback')
  return addDays(setHours(setMinutes(setSeconds(new Date(), 0), 59), 23), 365) // 1 year ahead as safe fallback
}

export const hasAvailability = (values, type = false) => {
  const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')
  return values.availability[key] !== false
}

export const isBookingAvailable = (values, type = false) => {
  const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')

  console.log("isBookingAvailable", key, values.availability);
  return values.availability[key]?.availabilities?.find(
    item => item.availableAt === 'AS_REQUESTED' || item.availableAt === 'DELAYED'
  ) !== undefined
}

export const isBookingAvailableDelayed = (values, type = false) => {
  const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')
  return values.availability[key]?.availabilities.find(item => item.availableAt === 'DELAYED') !== undefined
}

export const isBookingAvailableAsRequested = (values, type = false) => {
  const key = type || (values.pickupTime === '' ? 'direct' : 'prebook')
  return values.availability[key]?.availabilities.find(item => item.availableAt === 'AS_REQUESTED') !== undefined
}

// Maps old type names to normalized names used throughout the app
const PRICE_TYPE_ALIASES = { FIXED_PRICE: 'FIXED', TAXIMETER_PRICE: 'TAXIMETER' }

// Normalizes a priceType from either API format to a unified internal shape:
//   { type, paymentMethods: string[], voucherEnabled: bool, paymentMethodConfigurations: [{paymentMethod, voucherEnabled}] }
// Old format: { type, paymentMethods: string[], voucherEnabled: bool }
// New format: { type, paymentMethodConfigurations: [{ paymentMethod, voucherEnabled }] }
export const normalizePriceType = (pt) => {
  const type = PRICE_TYPE_ALIASES[pt.type] || pt.type
  if (pt.paymentMethodConfigurations) {
    const configs = pt.paymentMethodConfigurations.map(c => ({
      paymentMethod: c.paymentMethod,
      voucherEnabled: c.voucherEnabled === true || c.voucherEnabled === 'true',
    }))
    return {
      ...pt,
      type,
      paymentMethods: configs.map(c => c.paymentMethod),
      voucherEnabled: configs.some(c => c.voucherEnabled),
      paymentMethodConfigurations: configs,
    }
  }
  if (pt.paymentMethods) {
    return {
      ...pt,
      type,
      paymentMethodConfigurations: pt.paymentMethods.map(method => ({
        paymentMethod: method,
        voucherEnabled: pt.voucherEnabled === true,
      })),
    }
  }
  return { ...pt, type }
}

// Maps a Formik payment value + account object to the API payment method key
// used in paymentMethodConfigurations.
export const getPaymentMethodKey = (payment, account) => {
  if (payment === 'PayInCar') return 'PayInCar'
  if (payment?.startsWith('CreditCard')) return 'CreditCard'
  if (payment?.startsWith('Account')) {
    if (account?.type === 'TSAB') return 'TSABAccount'
    if (account?.type === 'TCA') return 'TravelClearingAccount'
  }
  return null
}

// Icon / title / subtitle for the SelectedPayment row, from the form's payment + account values.
// Shared by BookingSummary and the return trip sheet.
export const getPaymentDisplay = values => {
  const isCreditCard = !!values.payment && values.payment.indexOf('CreditCard') !== -1
  const icon = !values.payment || values.payment === 'DefaultNoSelect' ? 'PayInCar'
    : values.payment === 'PayInCar' ? 'PayInCar'
    : values.payment === 'Klarna' ? 'Klarna'
    : values.payment === 'ApplePay' ? 'ApplePay'
    : isCreditCard ? `Card${values.account?.cardType}`
    : 'TSAB'
  const title = values.payment === 'PayInCar' ? i18next.t('title-pay-in-car')
    : values.payment === 'Klarna' ? 'Klarna'
    : values.payment === 'ApplePay' ? 'Apple Pay'
    : isCreditCard ? values.account?.maskedAccount
    : values.account?.accountName
  const subtitle = isCreditCard ? values.account?.cardType
    : values.account?.accountNumber || null
  return { icon, title, subtitle, isCreditCard }
}

export const getVoucherValue = voucher => {
  if (voucher.valueType == 'AMOUNT') {
    return `${voucher.value}kr`
  }
  else if (voucher.valueType == 'PERCENTAGE') {
    return `${voucher.value}%`
  }
  else if (voucher.valueType == 'OPEN_AMOUNT') {
    return i18next.t('free-trip')
  }
}

export const getCarTypes = () => [
  { value: 'taxi', asValue:'taxi', title: i18next.t('cartype-taxi'), info: i18next.t('cartype-taxi-info'), capacity:4 },
  { value: 'estate', asValue:'taxi', title: i18next.t('cartype-estate'), info: i18next.t('cartype-estate-info'), capacity:4 },
  { value: 'largeCar', asValue:'largeCar', title: i18next.t('cartype-large-car'), info: i18next.t('cartype-large-car-info'), capacity:7 },
  { value: 'business', asValue:'business', title: i18next.t('cartype-business'), info: i18next.t('cartype-business-info'), capacity:3 },
  { value: 'delivery', asValue:'delivery', title: 'Bud', info: '' },
]

export const getCarType = serviceType => {
  return getCarTypes().find(c => c.value === serviceType)
}

export const hasFixedPrice = (priceComponents) => {
  if (priceComponents === undefined) return false
  return priceComponents.findIndex(c => c.type === 'fixedPrice') !== -1
}

export const getPriceFromEstimate = (prices, serviceType) => {
  return prices.find(p => p.serviceType === serviceType)?.finalPrice
    // ?.priceComponents.find(c => c.type === 'fixedPrice').amount
}

export const filterOptions = options => {
  if (options === undefined) return []
  return options.filter(value => ['pet', 'low', 'stationWagon', 'boosterSeat1', 'boosterSeat2', 'rearFacingChildSeat', 'electric'].includes(value))
}

export const getOptionText = value => {
  if (value === 'pet') return i18next.t('label-pet-option')
  if (value === 'low') return i18next.t('label-low-option')
  if (value === 'stationWagon') return i18next.t('label-stationwagon-option')
  if (value === 'boosterSeat1') return i18next.t('label-booster-1-option')
  if (value === 'boosterSeat2') return i18next.t('label-booster-2-option')
  if (value === 'rearFacingChildSeat') return i18next.t('label-childseat-option')
  if (value === 'electric') return i18next.t('label-electric-option')
}

export const getReceiptStatusText = booking => {
  switch (booking.receiptStatus) {
    // 100	AVAILABLE
    // 200	PENDING
    case '200':
      return i18next.t('info-receipt-pending')
    // 300	NO_RECEIPT_CANCELLED_ORDER (Cancelled order without upfront payment)
    // 400	NO_RECEIPT_FAILED_ORDER
    // 500	NO_RECEIPT (Since order is not paid by registered credit cards)
    // 600	NO_RECEIPT_TOO_OLD_ORDER
    // 700	NO_RECEIPT_LOST_BOM
    case '300':
    case '400':
    case '500':
    case '600':
    case '700':
      return i18next.t('info-receipt-not-available')
    // 800	NO_RECEIPT_REFUNDED_ORDER
    case '800':
      return i18next.t('info-receipt-refunded')
  }
}

export const getPackageText = value => {
  // Different copy depending on count
  const key = 'type-' + value.packageType
  return i18next.t(key, { count: value.count })
}

export const getTaxiAndEta = booking => {
  if (booking.bookingStatus.vehiclePlate !== undefined) {
    if(booking.bookingStatus.eta) {
      return i18next.t('title-car-and-eta', { taxi: booking.bookingStatus.vehiclePlate, vehicle: `${booking.bookingStatus.vehicleColor} ${booking.bookingStatus.vehicleMake} ${booking.bookingStatus.vehicleModel}`, eta: formatReadableDate(new Date(booking.bookingStatus.eta)) })
    } else {
      return i18next.t('title-car', { taxi: booking.bookingStatus.vehiclePlate, vehicle: `${booking.bookingStatus.vehicleColor} ${booking.bookingStatus.vehicleMake} ${booking.bookingStatus.vehicleModel}`})
    }
  }
  return null
}

export const hasTaxiAndEta = booking => {
  return [120, 310, 320].includes(booking?.bookingStatus?.statusCode) && booking?.bookingStatus?.vehiclePlate !== undefined
}

export const allowCancel = booking => {
  if(!booking || booking===undefined || booking===null) return false;
  return [110, 120, 121, 310, 320].includes(booking?.bookingStatus?.statusCode)
}

export const allowEdit = booking => {
  if(!booking || booking===undefined || booking===null) return false;
  if (!booking.editableUntil) return false;
  // Don't allow editing canceled bookings (status codes: 220=Cancelled, 610=Terminated, 620=TerminatedNoShow)
  if ([220, 610, 620].includes(booking?.bookingStatus?.statusCode)) return false;
  return new Date(booking.editableUntil) > new Date()
}

export const hasEnded = booking => {
  return [220, 510, 610, 620].includes(booking?.bookingStatus?.statusCode)
}

export const getStatusText = booking => {
  // TODO: This should be replaced
  const COPY = window.COPY

  // Check if booking and bookingStatus exist to prevent errors during polling
  if (!booking?.bookingStatus?.statusCode) return '-'

  if (booking.bookingStatus.statusCode == 110) return i18next.t('status-waiting-for-confirmation')

  // 120 Confirmed
  if (booking.bookingStatus.statusCode == 120 || booking.bookingStatus.statusCode == 121) {
    return booking.bookingType === 'PRE_BOOKING' ? i18next.t('status-pre-booked') : i18next.t('status-searching')
  }
  // 220 Cancelled
  if (booking.bookingStatus.statusCode == 220) return COPY.booking.status_cancelled

  // 310 Dispatched
  if (booking.bookingStatus.statusCode == 310) return COPY.booking.status_dispatched

  // 320 AtPickup
  if (booking.bookingStatus.statusCode == 320) return COPY.booking.status_at_pickup

  // 410 EnRoute
  if (booking.bookingStatus.statusCode == 410) return COPY.booking.status_en_route

  // 510 Done
  if (booking.bookingStatus.statusCode == 510) return COPY.booking.status_done

  // 610 Terminated
  if (booking.bookingStatus.statusCode == 610) return COPY.booking.status_cancelled

  // 620 TerminatedNoShow
  if (booking.bookingStatus.statusCode == 620) return COPY.booking.status_no_show

  return '-'
}

export const getStatusClassName = booking => {
  // Check if booking and bookingStatus exist to prevent errors during polling
  if (!booking?.bookingStatus?.statusCode) return ''

  // 120 Confirmed
  if (booking.bookingStatus.statusCode == 120 || booking.bookingStatus.statusCode == 121) {
    return booking.bookingType === 'PRE_BOOKING' ? 'prebooked' : 'searching'
  }
  // 220 Cancelled / 610 Terminated / 620 TerminatedNoShow
  if (booking.bookingStatus.statusCode == 220 || booking.bookingStatus.statusCode == 610 || booking.bookingStatus.statusCode == 620) return 'cancelled'

  // 310 Dispatched
  if (booking.bookingStatus.statusCode == 310) return 'arriving'

  // 320 AtPickup / 410 EnRoute
  if (booking.bookingStatus.statusCode == 320 || booking.bookingStatus.statusCode == 410) return 'in-car'

  // 510 Done
  if (booking.bookingStatus.statusCode == 510) return 'finished'
}
